/**
 * 線上商店／線上訂單頁面
 * 商品來源＝商品管理（與前台 POS 同源）；與 /preorder 面交預購頁面分離
 * /online-shopping、/online-order 共用此頁面，以 pathname 區分標題
 * 參考 preorder：點擊商品開啟 Drawer（shadcn/ui + Vaul），內含 Stepper + 加入購物車
 * 頂部分類 Tab 可快速篩選商品分類（category）
 */

import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProductsGetCategories } from '@pos/api-sdk';
import { useProducts } from '../../hooks/queries/useProducts';
import { useTenant } from '../../contexts/TenantContext';
import { useCheckoutConfig } from '../../hooks/useCheckoutConfig';
import { getApiHeaders } from '../../api/apiUtils';
import { normalizeImageUrl } from '../../utils/imageUrl';
import { ProductImageGallery } from '@/components/ProductImageGallery';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { formatTwdFromCents } from '@pos/shared/money';
import { calculateCartTotalInCents } from '@pos/shared/lib/pricing';
import {
  ProductDrawerContent,
  type ProductDrawerProduct,
  type ProductDrawerVariant,
} from '@/components/ProductDrawerContent';

type CartItem = {
  productId: string;
  productName: string;
  productPriceTwd: number;
  productImageUrl: string | null;
  quantity: number;
  remainingQuantity: number;
  /** 多規格品時：規格 ID */
  productVariantId?: string;
  /** 多規格品時：規格顯示文字（如「尺寸: l / 顏色: red」） */
  variantLabel?: string;
};

type DisplayProduct = {
  productId: string;
  productName: string;
  productDescription?: string;
  productPriceTwd: number;
  productImageUrl?: string | null;
  /** 多張圖片 URL（輪播用） */
  productImageUrls?: string[];
  remainingQuantity: number;
  hasVariants?: boolean;
  variants?: Array<{
    id: string;
    unitPriceTwd: number;
    combination?: Record<string, string>;
  }>;
};

export function OnlineShopPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, storage, isReady: tenantReady } = useTenant();
  const { config: checkoutConfig } = useCheckoutConfig(tenantId);

  const pageTitle = location.pathname.startsWith('/online-order')
    ? '線上點餐'
    : '線上商店';

  /** 頂部分類篩選（空字串＝全部） */
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  /** 取得商品分類列表（供頂部 Tab 使用） */
  const categoriesResult = useProductsGetCategories({
    fetch: { headers: getApiHeaders(tenantId ?? undefined) },
    swr: { enabled: !!tenantId },
  });
  const categoriesList: string[] = useMemo(() => {
    const res = categoriesResult.data;
    if (!res || res.status !== 200 || !res.data?.data) return [];
    return res.data.data;
  }, [categoriesResult.data]);

  const {
    products: productsFromApi,
    isLoading,
    isError,
  } = useProducts(
    {
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortDir: 'asc',
      is_active: 1,
      ...(selectedCategory ? { category: selectedCategory } : {}),
    },
    { enabled: !!tenantId, revalidateOnFocus: false, dedupingInterval: 5000 }
  );

  const displayProducts = useMemo<DisplayProduct[]>(() => {
    if (!productsFromApi?.length) return [];
    return productsFromApi.map(p => {
      const remainingQuantity =
        p.is_inventory_tracked === 1 && typeof p.stock_quantity === 'number'
          ? p.stock_quantity
          : 999;
      const hasVariants = Boolean(
        (p as { has_variants?: boolean }).has_variants &&
          (
            p as {
              variants?: Array<{
                id?: string;
                unit_price_twd: number;
                combination?: Record<string, string>;
              }>;
            }
          ).variants?.length
      );
      const rawVariants =
        (
          p as {
            variants?: Array<{
              id?: string;
              unit_price_twd: number;
              combination?: Record<string, string>;
            }>;
          }
        ).variants ?? [];
      const variants = hasVariants
        ? rawVariants.map(v => ({
            id: String(v.id ?? ''),
            unitPriceTwd: v.unit_price_twd,
            combination: v.combination ?? {},
          }))
        : undefined;
      const imageUrls = (p as { image_urls?: string[] }).image_urls;
      return {
        productId: p.id,
        productName: p.name,
        productDescription: p.description ?? undefined,
        productPriceTwd: p.unit_price_twd ?? p.list_price_twd ?? 0,
        productImageUrl: p.img_url ?? undefined,
        productImageUrls:
          Array.isArray(imageUrls) && imageUrls.length > 0
            ? imageUrls
            : undefined,
        remainingQuantity,
        hasVariants,
        variants,
      };
    });
  }, [productsFromApi]);

  /** 商品 Drawer（點擊卡片開啟，內含規格選擇＋Stepper＋加入購物車，與 preorder 共用 ProductDrawerContent） */
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<DisplayProduct | null>(
    null
  );
  const [drawerQuantity, setDrawerQuantity] = useState(1);
  /** Drawer 內多規格品時「依維度選擇」的目前值 */
  const [selectedByDimension, setSelectedByDimension] = useState<Record<
    string,
    string
  > | null>(null);

  /** 由 selectedByDimension 推得目前選中的規格（多規格品時） */
  const selectedDrawerVariant = useMemo((): ProductDrawerVariant | null => {
    const variants = selectedProduct?.variants ?? [];
    if (variants.length === 0 || !selectedByDimension) return null;
    const match = variants.find(v =>
      Object.entries(v.combination ?? {}).every(
        ([k, val]) => selectedByDimension[k] === val
      )
    );
    return match
      ? {
          id: match.id,
          unitPriceTwd: match.unitPriceTwd,
          combination: match.combination ?? {},
        }
      : null;
  }, [selectedProduct, selectedByDimension]);

  const [cart, setCart] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined' || !tenantReady || !storage) return [];
    try {
      const saved = storage.getItem<Partial<CartItem>[]>('cart');
      if (saved && Array.isArray(saved)) {
        return saved
          .filter(Boolean)
          .map(s => ({
            productId: String(s.productId ?? ''),
            productName: String(s.productName ?? ''),
            productPriceTwd: Number.isFinite(Number(s.productPriceTwd))
              ? Number(s.productPriceTwd)
              : 0,
            productImageUrl:
              s.productImageUrl === null
                ? null
                : String(s.productImageUrl ?? null),
            quantity:
              Number.isFinite(Number(s.quantity)) && Number(s.quantity) > 0
                ? Number(s.quantity)
                : 0,
            remainingQuantity: Number.isFinite(Number(s.remainingQuantity))
              ? Number(s.remainingQuantity)
              : 999,
            productVariantId:
              s.productVariantId != null
                ? String(s.productVariantId)
                : undefined,
            variantLabel:
              s.variantLabel != null ? String(s.variantLabel) : undefined,
          }))
          .filter(item => item.quantity > 0);
      }
    } catch {
      // ignore
    }
    return [];
  });

  const cartToPersist = useMemo(
    () => cart.filter(item => item.quantity > 0),
    [cart]
  );
  useEffect(() => {
    if (!tenantReady || !storage) return;
    try {
      if (cartToPersist.length > 0) {
        storage.setItem('cart', cartToPersist);
      } else {
        storage.removeItem('cart');
      }
    } catch {
      // ignore
    }
  }, [tenantReady, storage, cartToPersist]);

  /** 開啟商品 Drawer（點擊卡片時呼叫；多規格品時預設選第一個規格） */
  const openProductDrawer = (product: DisplayProduct) => {
    if (product.remainingQuantity <= 0) {
      toast.error('該商品已售罄');
      return;
    }
    const hasVariants = Boolean(
      product.hasVariants && product.variants?.length
    );
    const firstCombination =
      hasVariants && product.variants?.[0]
        ? (product.variants[0].combination ?? {})
        : null;
    const firstVariantId = hasVariants ? (product.variants?.[0]?.id ?? '') : '';
    setSelectedByDimension(firstCombination);
    const existing = cart.find(
      item =>
        item.productId === product.productId &&
        (item.productVariantId ?? '') === firstVariantId
    );
    setSelectedProduct(product);
    setDrawerQuantity(existing ? existing.quantity : 1);
    setProductDrawerOpen(true);
  };

  const drawerStepperAtMax =
    selectedProduct != null &&
    drawerQuantity >= selectedProduct.remainingQuantity;
  const drawerStepperAtMin = drawerQuantity <= 1;

  const handleDrawerStepperMinus = () =>
    setDrawerQuantity(q => Math.max(1, q - 1));
  const handleDrawerStepperPlus = () => {
    if (selectedProduct)
      setDrawerQuantity(q =>
        Math.min(selectedProduct.remainingQuantity, q + 1)
      );
  };

  /** Drawer 內加入／更新購物車後關閉（多規格品時須先選規格） */
  const handleDrawerAddToCart = () => {
    if (!selectedProduct) return;
    if (
      selectedProduct.hasVariants &&
      selectedProduct.variants?.length &&
      !selectedDrawerVariant
    ) {
      toast.error('請選擇規格');
      return;
    }
    if (drawerQuantity > selectedProduct.remainingQuantity) {
      toast.error('超過該商品最大可選數量');
      return;
    }
    const variantId = selectedDrawerVariant?.id ?? '';
    const priceTwd =
      selectedDrawerVariant?.unitPriceTwd ?? selectedProduct.productPriceTwd;
    const variantLabel = selectedDrawerVariant
      ? Object.entries(selectedDrawerVariant.combination ?? {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(' / ')
      : undefined;
    const existing = cart.find(
      item =>
        item.productId === selectedProduct.productId &&
        (item.productVariantId ?? '') === variantId
    );
    if (existing) {
      setCart(prev =>
        prev.map(item =>
          item.productId === selectedProduct.productId &&
          (item.productVariantId ?? '') === variantId
            ? { ...item, quantity: drawerQuantity }
            : item
        )
      );
    } else {
      setCart(prev => [
        ...prev,
        {
          productId: selectedProduct.productId,
          productName: selectedProduct.productName,
          productPriceTwd: priceTwd,
          productImageUrl: selectedProduct.productImageUrl ?? null,
          quantity: drawerQuantity,
          remainingQuantity: selectedProduct.remainingQuantity,
          productVariantId: variantId || undefined,
          variantLabel,
        },
      ]);
    }
    setProductDrawerOpen(false);
    toast.success('已加入購物車');
  };

  const handleUpdateQuantity = (
    productId: string,
    delta: number,
    productVariantId?: string
  ) => {
    setCart(prev => {
      const item = prev.find(
        i =>
          i.productId === productId &&
          (i.productVariantId ?? '') === (productVariantId ?? '')
      );
      if (!item) return prev;
      const next = item.quantity + delta;
      if (next <= 0)
        return prev.filter(
          i =>
            !(
              i.productId === productId &&
              (i.productVariantId ?? '') === (productVariantId ?? '')
            )
        );
      return prev.map(i =>
        i.productId === productId &&
        (i.productVariantId ?? '') === (productVariantId ?? '')
          ? { ...i, quantity: next }
          : i
      );
    });
  };

  const handleRemoveFromCart = (
    productId: string,
    productVariantId?: string
  ) => {
    setCart(prev =>
      prev.filter(
        item =>
          !(
            item.productId === productId &&
            (item.productVariantId ?? '') === (productVariantId ?? '')
          )
      )
    );
  };

  /** 小計（分），與 preorder / online_order 同一公式 SSOT */
  const subtotalCents = cart.reduce(
    (sum, item) => sum + item.productPriceTwd * item.quantity,
    0
  );
  const taxRateBps = checkoutConfig?.taxRateBps ?? 500;
  const discountCents = 0;
  const { taxDisplayCents, totalPayableCents } = calculateCartTotalInCents(
    subtotalCents,
    discountCents,
    taxRateBps
  );

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('請至少選擇一個商品');
      return;
    }
    if (!tenantId) {
      toast.error('無法取得商家資訊，請重新整理後再試');
      return;
    }
    const query = `?tenantId=${encodeURIComponent(tenantId)}`;
    const isOnlineOrder = location.pathname.startsWith('/online-order');
    if (isOnlineOrder) {
      navigate(`/online-order/checkout${query}`, {
        state: {
          cart,
          displayProducts,
          formState: {
            pickupDate: '',
            deliveryMethod: 'store_pickup',
            pickupType: 'takeout',
            shippingAddress: null,
            convenienceStoreInfo: null,
            customerName: '',
            customerPhone: '',
            customerNote: '',
            invoiceType: 'personal',
            taxId: '',
            invoiceTitle: '',
            carrierType: 'member',
            carrierNum: '',
            loveCode: '',
            email: '',
          },
        },
      });
    } else {
      navigate(`/online-shopping/checkout${query}`, {
        state: { cart, displayProducts },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="ml-3 text-gray-600">載入商品中...</p>
      </div>
    );
  }

  if (isError || !displayProducts.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <p className="text-gray-600">目前尚無商品，請稍後再試。</p>
      </div>
    );
  }

  const coverImageUrl = checkoutConfig?.coverImageUrl ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-24">
      {/* 標題區：有封面圖時當背景，無則白底 */}
      <div
        className={`relative shadow-sm sticky top-0 z-10 border-b border-gray-200 bg-cover bg-center ${
          coverImageUrl ? 'min-h-28' : 'bg-white'
        }`}
        style={
          coverImageUrl
            ? { backgroundImage: `url(${coverImageUrl})` }
            : undefined
        }
      >
        {coverImageUrl ? (
          <div className="absolute inset-0 bg-black/40" aria-hidden />
        ) : null}
        <div className="relative px-4 py-3">
          <h1
            className={`text-[1.25rem] font-bold ${
              coverImageUrl ? 'text-white drop-shadow-md' : 'text-gray-900'
            }`}
          >
            {pageTitle}
          </h1>
        </div>
        {/* 頂部分類快速篩選 */}
        <nav
          className="relative flex gap-1 overflow-x-auto px-4 pb-3"
          aria-label="商品分類"
        >
          <button
            type="button"
            onClick={() => setSelectedCategory('')}
            className={`
              flex-shrink-0 px-4 py-2 text-sm font-medium rounded-full transition-colors
              ${selectedCategory === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
            `}
          >
            全部
          </button>
          {categoriesList.map(cat => {
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`
                  flex-shrink-0 px-4 py-2 text-sm font-medium rounded-full transition-colors
                  ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
              >
                {cat}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="px-4 py-4">
        <div className="space-y-3">
          {displayProducts.map((product, idx) => {
            const cartQtyForProduct = cart
              .filter(item => item.productId === product.productId)
              .reduce((sum, item) => sum + item.quantity, 0);
            const isOutOfStock = product.remainingQuantity <= 0;

            return (
              <div
                key={`${product.productId}-${idx}`}
                onClick={() => {
                  if (!isOutOfStock) openProductDrawer(product);
                }}
                className={`bg-white rounded-lg shadow-md overflow-hidden flex select-none ${
                  isOutOfStock ? 'opacity-70' : 'active:scale-[0.98]'
                }`}
              >
                <div className="w-24 h-24 flex-shrink-0 bg-gray-100 flex items-center justify-center relative overflow-hidden rounded-l">
                  <ProductImageGallery
                    primaryUrl={product.productImageUrl}
                    imageUrls={product.productImageUrls}
                    normalizedUrls={[
                      normalizeImageUrl(product.productImageUrl),
                      ...(product.productImageUrls ?? []).map(u =>
                        normalizeImageUrl(u)
                      ),
                    ].filter((u): u is string => Boolean(u))}
                    alt={product.productName}
                    thumbnail
                    className="w-full h-full"
                    imageClassName="w-full h-full rounded-none"
                  />
                  {cartQtyForProduct > 0 && (
                    <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                      {cartQtyForProduct}
                    </div>
                  )}
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">
                        SOLD OUT
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 p-3 flex flex-col justify-center">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-xs font-bold text-gray-900 line-clamp-1 flex-1 min-w-0">
                      {product.productName}
                    </h3>
                    <p className="text-xs font-semibold text-blue-600 flex-shrink-0">
                      {formatTwdFromCents(product.productPriceTwd)}
                    </p>
                  </div>
                  {product.productDescription && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {product.productDescription}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 商品 Drawer：點擊卡片開啟，共用 ProductDrawerContent（規格＋Stepper＋加入購物車） */}
      <Drawer
        open={productDrawerOpen}
        onOpenChange={open => {
          setProductDrawerOpen(open);
          if (!open) setSelectedByDimension(null);
        }}
      >
        <DrawerContent noDrag>
          {selectedProduct &&
            (() => {
              const primaryNorm = normalizeImageUrl(
                selectedProduct.productImageUrl
              );
              const urlsNorm = (selectedProduct.productImageUrls ?? []).map(u =>
                normalizeImageUrl(u)
              );
              const allNorm = [primaryNorm, ...urlsNorm].filter(
                (u): u is string => Boolean(u)
              );
              const drawerProduct: ProductDrawerProduct = {
                productName: selectedProduct.productName,
                productDescription: selectedProduct.productDescription ?? null,
                imageUrl: primaryNorm ?? null,
                imageUrls: allNorm.length > 1 ? allNorm.slice(1) : undefined,
                productPriceTwd: selectedProduct.productPriceTwd,
                hasVariants: selectedProduct.hasVariants,
                variants: selectedProduct.variants,
              };
              return (
                <ProductDrawerContent
                  product={drawerProduct}
                  selectedByDimension={selectedByDimension}
                  setSelectedByDimension={setSelectedByDimension}
                  selectedVariant={selectedDrawerVariant}
                  quantity={drawerQuantity}
                  onQuantityMinus={handleDrawerStepperMinus}
                  onQuantityPlus={handleDrawerStepperPlus}
                  onAddToCart={handleDrawerAddToCart}
                  maxQuantity={selectedProduct.remainingQuantity}
                  stepperAtMin={drawerStepperAtMin}
                  stepperAtMax={drawerStepperAtMax}
                  addToCartLabel="加入購物車"
                />
              );
            })()}
        </DrawerContent>
      </Drawer>

      {/* 購物車摘要：項數 SSOT = cart.length（與 preorder / online_order 一致） */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-20">
          <div className="px-4 py-3 max-h-64 overflow-y-auto">
            <h3 className="text-[0.875rem] font-medium text-gray-900 mb-2">
              購物車 ({cart.length} 項)
            </h3>
            <div className="space-y-2">
              {cart.map((item, idx) => (
                <div
                  key={`${item.productId}-${item.productVariantId ?? ''}-${idx}`}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.productName}
                      {item.variantLabel ? (
                        <span className="text-gray-600 font-normal">
                          {' '}
                          · {item.variantLabel}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatTwdFromCents(item.productPriceTwd)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpdateQuantity(
                          item.productId,
                          -1,
                          item.productVariantId
                        );
                      }}
                      className="w-7 h-7 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm"
                    >
                      −
                    </button>
                    <span className="w-8 text-center text-sm font-medium">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpdateQuantity(
                          item.productId,
                          1,
                          item.productVariantId
                        );
                      }}
                      className="w-7 h-7 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm"
                      disabled={item.quantity >= item.remainingQuantity}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleRemoveFromCart(
                          item.productId,
                          item.productVariantId
                        );
                      }}
                      className="w-6 h-6 text-red-500 hover:text-red-700"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* 小計、稅金、應付（與 preorder PreorderCartSummary 排版一致） */}
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-[0.8125rem] text-gray-600">
              <div className="flex justify-between">
                <span>小計</span>
                <span className="tabular-nums">
                  {formatTwdFromCents(subtotalCents)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>稅金 ({taxRateBps / 100}%)</span>
                <span className="tabular-nums">
                  {formatTwdFromCents(taxDisplayCents)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-[0.875rem] font-medium text-gray-900">
                  應付
                </span>
                <span className="text-[1.125rem] font-bold text-blue-600 tabular-nums">
                  {formatTwdFromCents(totalPayableCents)}
                </span>
              </div>
              <Button
                type="button"
                onClick={handleCheckout}
                className="shrink-0"
              >
                結帳
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
